import React, { useCallback, useEffect, useState } from "react";
import PaymentsAppLayout from "../../Components/Molecules/PaymentsAppLayout";
import Table, {
  Column,
  DataSourceItem,
  RowSelectionActionOn,
  RowSelectionOptions
} from "../../Components/Atoms/Table";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import LoadingPage from "../LoadingPage";
import TablePaginationFooter from "../../Components/Atoms/Table/TablePaginationFooter";
import { useInvoiceStore } from "../../Store/Invoice";
import { PAGINATION_KEYS, usePagination } from "../../Store/Pagination";
import { toLocalDateString } from "../../Utils/DateUtils";
import PrivateComponentLoadingPage from "../LoadingPage/PrivateComponentLoadingPage";
import Tag from "../../Components/Atoms/Tag";
import {
  Invoice,
  InvoiceStatus,
  PaymentsInvoiceAutoCollectionStatus
} from "../../Contracts/Invoice";
import { createSortFunction } from "../../Utils/TableUtils";
import { PaymentRoutes } from "../../Contracts/Routes";
import InvoiceListPageFilters from "./InvoiceListPageFilters";
import InvoiceListActions from "../../Components/Molecules/InvoiceListActions";
import { ReactComponent as DropdownDownArrowIcon } from "../../Assets/ActionIcons/DropdownDownArrowIcon.svg";
import { ReactComponent as NoAvailablePaymentMethod } from "../../Assets/CustomerDetailPage/NoAvailablePaymentMethod.svg";
import { ReactComponent as ExclamationWarningIcon } from "../../Assets/ActionIcons/ExclamationWarningIcon.svg";
import { ReactComponent as CloseIcon } from "../../Assets/ActionIcons/CloseIcon.svg";
import Popover, { PopoverPosition } from "../../Components/Atoms/Popover";
import BulkActionsModal from "../../Components/Molecules/BulkActionsModal";
import TableBulkActions from "../../Components/Atoms/Table/TableBulkActions";
import Modal from "../../Components/Atoms/Modal";
import { useCustomerStore } from "../../Store/Customer";
import { PaymentMethodDisplayAttributes } from "../../Contracts/Finance";
import { useOnboardingStore } from "../../Store/Onboarding";
import { useDebounce } from "../../Utils/ActionUtils";

const getDropDownActionProps = (
  enableCollectNow: boolean
): {
  label: string;
  value: string;
  disabled: boolean;
  disabledMessage?: string;
  disabledMessagePosition?: PopoverPosition;
  getWarningMessage: (
    on: RowSelectionActionOn,
    rowsSelected: number,
    totalRows: number
  ) => string | null;
  actionDescription: string;
}[] => [
  {
    label: "Send Payment Link",
    value: "send_payment_link",
    disabled: false,
    getWarningMessage: (on, rowsSelected, totalRows) =>
      `Are you sure you want to send payment links to ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected invoices to the customer contacts?\n\nNote: This action is not applicable for paid invoices.`,
    actionDescription: "Sending payment links for selected invoices"
  },
  {
    label: "Enable Invoice Emails",
    value: "enable_invoice_emails",
    disabled: false,
    getWarningMessage: (on, rowsSelected, totalRows) =>
      `Are you sure you want to enable invoice emails for ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected invoices?\n\nNote: This action is not applicable for paid invoices.`,
    actionDescription: "Enabling invoice emails for selected invoices"
  },
  {
    label: "Disable Invoice Emails",
    value: "disable_invoice_emails",
    disabled: false,
    getWarningMessage: (on, rowsSelected, totalRows) =>
      `Are you sure you want to disable invoice emails for ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected invoices?\n\nNote: This action is not applicable for paid invoices.`,
    actionDescription: "Disabling invoice emails for selected invoices"
  },
  {
    label: "Enable Invoice Emails and Auto Collect",
    value: "enable_invoice_emails_auto_collect",
    disabled: false,
    getWarningMessage: (on, rowsSelected, totalRows) =>
      `Are you sure you want to enable invoice emails and auto collect for ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected invoices?\n\nNote: This action is not applicable for paid invoices.`,
    actionDescription: "Enabling invoice emails and auto collect for selected invoices"
  },
  {
    label: "Disable Invoice Emails and Auto Collect",
    value: "disable_invoice_emails_auto_collect",
    disabled: false,
    getWarningMessage: (on, rowsSelected, totalRows) =>
      `Are you sure you want to disable invoice emails and auto collect for ${
        on === "all" ? `all ${totalRows}` : rowsSelected
      } selected invoices?\n\nNote: This action is not applicable for paid invoices.`,
    actionDescription: "Disabling invoice emails and auto collect for selected invoices"
  },
  {
    label: "Sync Invoices",
    value: "sync_invoices",
    disabled: false,
    getWarningMessage: () => null,
    actionDescription: "Syncing selected invoices"
  },
  {
    label: "Collect Now",
    value: "collect_now",
    disabled: !enableCollectNow,
    disabledMessage: "Selected invoices must be of the same client",
    disabledMessagePosition: "left",
    getWarningMessage: () => null,
    actionDescription: "Collecting selected invoices"
  }
];

const InvoiceListPage = () => {
  const [sortParamValue, setSortParamValue] = useState<string>("issue_date:desc");
  const [dropDownActionProps, setDropDownActionProps] = useState<
    {
      label: string;
      value: string;
      disabled: boolean;
      disabledMessage?: string;
      disabledMessagePosition?: PopoverPosition;
      getWarningMessage: (
        on: RowSelectionActionOn,
        rowsSelected: number,
        totalRows: number
      ) => string | null;
      actionDescription: string;
    }[]
  >(getDropDownActionProps(true));
  const {
    isLoading,
    invoices,
    invoicesCount,
    getInvoices,
    getAllInvoices,
    sendInvoicePaymentLink,
    toggleEmailOrAutoCollectSettings,
    collectNow,
    syncInvoiceNow,
    invoiceBaseUrl
  } = useInvoiceStore();
  const { getAdyenKYCDetails } = useOnboardingStore();
  const { customerDetailEntity, getCustomerDetails } = useCustomerStore();
  const { limit, page, setLimit, setPage } = usePagination(PAGINATION_KEYS.INVOICE_LIST, {
    limit: 25,
    page: 1
  });
  const [openMoreMenu, setOpenMoreMenu] = useState<number>(-1);
  const [collectNowModal, setCollectNowModal] = React.useState<{
    open: boolean;
    isLoading: boolean;
    selectedToken?: number;
  }>({ open: false, isLoading: false });

  const columns: Column<Invoice>[] = [
    {
      title: "Invoice Number",
      dataIndex: "invoice",
      key: "invoice",
      sortClicked: createSortFunction("invoice_number", setSortParamValue),
      width: "w-44",
      render: d => (
        <a
          href={`${invoiceBaseUrl}${d.data.collectible_invoice_id}`}
          className="text-blue-600"
          target="_blank"
          rel="noreferrer noopener"
        >
          {d.invoice}
        </a>
      )
    },
    {
      title: "Invoice Status",
      dataIndex: "status",
      key: "status",
      render: d => (
        <div className="flex items-center space-x-2">
          {d.data.payment_is_processing ? (
            <Tag color="orange">Processing</Tag>
          ) : (
            <Tag
              color={
                d.data.status === InvoiceStatus.Overdue
                  ? "red"
                  : d.data.status === InvoiceStatus.Paid
                    ? "lime"
                    : d.data.status === InvoiceStatus.Open
                      ? "blue"
                      : "gray"
              }
            >
              {d.data.status}
            </Tag>
          )}
          {d.data.is_invoice_associated_with_chargeback && (
            <Popover
              message={
                <div>
                  The invoice is associated with a chargeback.{" "}
                  <a
                    href="https://help.zomentum.com/support/solutions/articles/44002440952-how-is-a-chargeback-handled-in-my-accounting-software-"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline cursor-pointer"
                  >
                    Learn More
                  </a>
                </div>
              }
            >
              <ExclamationWarningIcon className="w-3 h-3 text-red-600" />
            </Popover>
          )}
        </div>
      )
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: d => (
        <a
          href={`${window.location.origin}${PaymentRoutes.CUSTOMERS}/${d.data.customer_id}`}
          className="text-blue-600"
        >
          {d.client}
        </a>
      )
    },
    {
      title: "Email Status",
      dataIndex: "email",
      key: "email",
      render: d => (
        <Tag color={d.data.invoice_emails_enabled ? "lime" : "gray"}>
          {d.data.invoice_emails_enabled ? "Enabled" : "Disabled"}
        </Tag>
      )
    },
    {
      title: "Last Sent On",
      dataIndex: "lastSentOn",
      key: "lastSentOn",
      sortClicked: createSortFunction("last_sent_on", setSortParamValue),
      width: "w-44",
      render: d => (
        <span title={toLocalDateString(d.data.last_sent_on, true)}>
          {toLocalDateString(d.data.last_sent_on)}
        </span>
      )
    },
    {
      title: "Auto Collect",
      dataIndex: "autoCollect",
      key: "autoCollect",
      render: d => {
        return <Tag color={d.autoCollect === "Disabled" ? "gray" : "lime"}>{d.autoCollect}</Tag>;
      }
    },
    {
      title: "Date Issued",
      dataIndex: "dateIssued",
      key: "dateIssued",
      sortClicked: createSortFunction("issue_date", setSortParamValue),
      width: "w-44",
      render: d => (
        <span title={toLocalDateString(d.data.issue_date, true)}>
          {toLocalDateString(d.data.issue_date)}
        </span>
      ),
      defaultSortOrder: "desc"
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      sortClicked: createSortFunction("due_date", setSortParamValue),
      width: "w-44",
      render: d => (
        <span title={toLocalDateString(d.data.due_date, true)}>
          {toLocalDateString(d.data.due_date)}
        </span>
      )
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: d => (
        <InvoiceListActions
          invoice={d.data}
          open={d.data.id === openMoreMenu}
          onClose={() => {
            setOpenMoreMenu(-1);
          }}
          onOpen={() => setOpenMoreMenu(d.data.id)}
        />
      ),
      align: "center",
      width: "w-12"
    }
  ];

  const [filter, setFilter] = useState<string>("");
  const [search, setSearch] = useState<string | undefined>(undefined);
  const debouncedSearch = useDebounce(search, 1500);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setSearch(event.target.value);
      setPage(1);
    } else {
      setSearch(undefined);
      setPage(1);
    }
  };

  const [selected, setSelected] = useState<{
    selectedRowsKeys: string[];
    selectedRows: DataSourceItem<Invoice>[];
  }>({
    selectedRowsKeys: [],
    selectedRows: []
  });
  const [bulkActionProps, setBulkActionProps] = useState<{
    isOpen: boolean;
    data: DataSourceItem<Invoice>[];
    description?: string;
    title?: string;
    action?: string;
  }>({
    isOpen: false,
    data: []
  });

  const { getAccessTokenSilently } = useAuth0();

  const refreshThings = useCallback(async () => {
    await Promise.all([
      getInvoices(getAccessTokenSilently, limit, sortParamValue, page, filter, debouncedSearch),
      getAdyenKYCDetails(getAccessTokenSilently)
    ]);
  }, [
    getInvoices,
    getAdyenKYCDetails,
    getAccessTokenSilently,
    limit,
    sortParamValue,
    page,
    filter,
    debouncedSearch
  ]);

  useEffect(() => {
    refreshThings();
  }, [refreshThings, limit, sortParamValue, page, debouncedSearch]);

  const convertInvoiceToDataSourceItem = (invoice: Invoice): DataSourceItem<Invoice> => ({
    key: invoice.id.toString(),
    invoice: invoice.invoice_number,
    client: invoice.customer_name,
    email: invoice.invoice_emails_enabled ? "Enabled" : "Disabled",
    lastSentOn: toLocalDateString(invoice.last_sent_on),
    autoCollect:
      invoice.payments_invoice_auto_collection_status ===
      PaymentsInvoiceAutoCollectionStatus.Pending
        ? "Enabled"
        : "Disabled",
    dateIssued: toLocalDateString(invoice.issue_date),
    dueDate: toLocalDateString(invoice.due_date),
    data: invoice
  });

  const rowSelectOptions: RowSelectionOptions<Invoice> = {
    enableSelection: true,
    selected: selected.selectedRowsKeys,
    onRowSelectionChange: (selectedRowsKeys, selectedRows) => {
      setSelected({
        selectedRowsKeys,
        selectedRows
      });
      if (selectedRows.length > 0) {
        const customerId = selectedRows[0]?.data.customer_id;
        const allCustomerIdSame = selectedRows.every(row => row.data.customer_id === customerId);
        const disabled =
          dropDownActionProps.find(option => option.value === "collect_now")?.disabled || false;
        if (disabled && allCustomerIdSame) {
          setDropDownActionProps(getDropDownActionProps(allCustomerIdSame));
        } else if (!disabled && !allCustomerIdSame) {
          setDropDownActionProps(getDropDownActionProps(allCustomerIdSame));
        }
      }
    }
  };
  const onRowSelectionAction = async (selectedValue: string | number, on: RowSelectionActionOn) => {
    const targetValue = dropDownActionProps.find(option => option.value === selectedValue);
    const rowsToTarget =
      on === "all"
        ? (
            await getAllInvoices(
              getAccessTokenSilently,
              limit,
              sortParamValue,
              filter,
              debouncedSearch
            )
          ).map(invoice => convertInvoiceToDataSourceItem(invoice))
        : selected.selectedRows || [];
    if (targetValue) {
      switch (selectedValue) {
        case "send_payment_link":
        case "enable_invoice_emails":
        case "disable_invoice_emails":
        case "enable_invoice_emails_auto_collect":
        case "disable_invoice_emails_auto_collect":
        case "sync_invoices":
          const warningMessage = targetValue.getWarningMessage(
            on,
            selected.selectedRowsKeys.length,
            invoicesCount || 0
          );

          if ((warningMessage && window.confirm(warningMessage)) || !warningMessage) {
            setBulkActionProps({
              isOpen: true,
              data: rowsToTarget,
              description: targetValue?.actionDescription,
              title: targetValue?.label,
              action: selectedValue
            });
          }
          break;
        case "collect_now":
          setCollectNowModal({ open: true, isLoading: true });
          const customerId = rowsToTarget[0]?.data.customer_id;
          setBulkActionProps({
            isOpen: false,
            data: rowsToTarget,
            description: targetValue?.actionDescription,
            title: targetValue?.label,
            action: selectedValue
          });
          await getCustomerDetails(getAccessTokenSilently, customerId?.toString());
          setCollectNowModal({ open: true, isLoading: false });
          break;
      }
    } else {
      window.alert("Something went wrong");
    }
  };

  const onDataAction = async (data: DataSourceItem<Invoice>) => {
    switch (bulkActionProps.action) {
      case "send_payment_link":
        if (data.data.status === InvoiceStatus.Paid) {
          return Promise.reject(`Invoice ${data.data.invoice_number} is already paid.`);
        } else {
          return await sendInvoicePaymentLink(
            getAccessTokenSilently,
            data.data.customer_id.toString(),
            data.data.id.toString()
          );
        }
      case "enable_invoice_emails":
        if (data.data.status === InvoiceStatus.Paid) {
          return Promise.reject(`Invoice ${data.data.invoice_number} is already paid.`);
        } else {
          return await toggleEmailOrAutoCollectSettings(
            getAccessTokenSilently,
            data.data.customer_id.toString(),
            data.data.id.toString(),
            true,
            data.data.payments_invoice_auto_collection_status ===
              PaymentsInvoiceAutoCollectionStatus.Pending
          );
        }
      case "disable_invoice_emails":
        return await toggleEmailOrAutoCollectSettings(
          getAccessTokenSilently,
          data.data.customer_id.toString(),
          data.data.id.toString(),
          false,
          data.data.payments_invoice_auto_collection_status ===
            PaymentsInvoiceAutoCollectionStatus.Pending
        );
      case "enable_invoice_emails_auto_collect":
        return await toggleEmailOrAutoCollectSettings(
          getAccessTokenSilently,
          data.data.customer_id.toString(),
          data.data.id.toString(),
          true,
          true
        );
      case "disable_invoice_emails_auto_collect":
        return await toggleEmailOrAutoCollectSettings(
          getAccessTokenSilently,
          data.data.customer_id.toString(),
          data.data.id.toString(),
          false,
          false
        );
      case "sync_invoices":
        return await syncInvoiceNow(
          getAccessTokenSilently,
          data.data.customer_id.toString(),
          data.data.id.toString()
        );
      case "collect_now":
        if (data.data.status === InvoiceStatus.Paid) {
          return Promise.reject(`Invoice ${data.data.invoice_number} is already paid.`);
        } else if (collectNowModal.selectedToken) {
          return await collectNow(
            getAccessTokenSilently,
            data.data.id.toString(),
            collectNowModal.selectedToken.toString()
          );
        } else {
          return Promise.reject("No payment method selected");
        }
    }
  };

  const dataSource: DataSourceItem<Invoice>[] = invoices.map(invoice =>
    convertInvoiceToDataSourceItem(invoice)
  );

  const collectNowAvailablePaymentMethods =
    customerDetailEntity?.available_payment_methods?.data
      .sort((a, _) => (a.primary ? -1 : 1))
      .map(m => {
        return { ...m, ...new PaymentMethodDisplayAttributes(m) };
      }) || [];

  return (
    <PaymentsAppLayout>
      <div className="flex flex-col h-[calc(100vh-9rem)]">
        {selected.selectedRowsKeys.length > 0 ? (
          <TableBulkActions
            selectItemsLength={selected.selectedRowsKeys.length}
            dataSourceLength={dataSource.length}
            allowAllSelection={true}
            clearSelections={() =>
              setSelected({
                selectedRowsKeys: [],
                selectedRows: []
              })
            }
            totalRows={invoicesCount}
            onRowSelectionAction={onRowSelectionAction}
            actionsDropdownProps={{
              options: dropDownActionProps,
              position: "bottom-right",
              anchor: (
                <div className="w-full px-2 py-1 rounded border flex items-center space-x-4 text-gray-600">
                  <div>Actions</div>
                  <DropdownDownArrowIcon className="w-3 h-3" />
                </div>
              )
            }}
          />
        ) : (
          <div className="flex items-center space-x-2 mt-2">
            <input
              placeholder="Search for an invoice.."
              value={search || ""}
              onChange={handleSearchInputChange}
              className={`border border-gray-300 rounded p-1 my-1 w-48`}
            />
            {search && (
              <CloseIcon
                className="h-3 w-3 hover:scale-110 transition-all duration-500 hover:text-red-600 cursor-pointer"
                onClick={() => setSearch(undefined)}
              />
            )}
            <InvoiceListPageFilters setFilter={setFilter} />
          </div>
        )}
        <Modal
          isOpen={collectNowModal.open}
          onClose={() => {
            setCollectNowModal({
              ...collectNowModal,
              open: false
            });
            setBulkActionProps({
              isOpen: false,
              data: [],
              action: undefined
            });
          }}
          onOk={() => {
            if (collectNowModal.selectedToken) {
              setCollectNowModal({
                ...collectNowModal,
                open: false,
                isLoading: false
              });
              setBulkActionProps({
                ...bulkActionProps,
                isOpen: true
              });
            }
          }}
          title="Collect Now"
          topOffset="10rem"
          hideCancelButton={true}
          okText="Collect Now"
          size="small"
        >
          <div className="p-4 flex flex-col text-left space-y-4 flex-grow">
            <div>Available Payment Methods</div>
            {collectNowModal.isLoading ? (
              <div className="flex flex-grow items-center">
                <LoadingPage />
              </div>
            ) : collectNowAvailablePaymentMethods.length > 0 ? (
              <div className="flex-grow h-[21.9rem] overflow-y-auto">
                {collectNowAvailablePaymentMethods.map((method, index) => (
                  <div
                    className={`relative flex items-center justify-between cursor-pointer ${
                      index !== collectNowAvailablePaymentMethods.length - 1 ? "border-b" : ""
                    }`}
                    key={method.key}
                    onClick={() =>
                      setCollectNowModal({
                        ...collectNowModal,
                        selectedToken: method.id
                      })
                    }
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        checked={method.id === collectNowModal.selectedToken}
                        onChange={() => {}}
                      />
                      <div className="flex items-center p-2" key={method.key}>
                        <img src={method.logoSrc} alt={method.displayName} className="mr-4" />
                        <div className="flex flex-col">
                          <span>{method.detailsDisplayLine1}</span>
                          <span>{method.detailsDisplayLine2}</span>
                        </div>
                        {method.primary && (
                          <div className="py-1 px-2 mx-4 mb-4 bg-blue-100 text-blue-600 border border-blue-400 text-xs rounded">
                            Primary
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <NoAvailablePaymentMethod className="self-center" />
                <div className="text-center">Client has not added any payment method</div>
              </>
            )}
          </div>
        </Modal>
        <BulkActionsModal<Invoice>
          {...bulkActionProps}
          dataValueKey="invoice_number"
          size="medium"
          topOffset="10rem"
          onClose={() => {
            setBulkActionProps({
              isOpen: false,
              data: [],
              action: undefined
            });
            setSelected({
              selectedRowsKeys: [],
              selectedRows: []
            });
          }}
          onDataAction={onDataAction}
          onAllActionsComplete={refreshThings}
        />
        <div className="flex flex-grow flex-col overflow-y-auto">
          {isLoading && invoices.length === 0 ? (
            <LoadingPage />
          ) : (
            <Table
              dataSource={dataSource}
              columns={columns}
              isLoading={isLoading}
              rowSelectionOptions={rowSelectOptions}
            />
          )}
          {!isLoading && invoices.length === 0 && (
            <div className="text-center text-gray-400 mt-12">No invoices found</div>
          )}
        </div>
        <TablePaginationFooter
          limit={limit}
          setLimit={setLimit}
          setPage={setPage}
          totalRecords={invoicesCount}
          page={page}
          isLoading={isLoading}
        />
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(InvoiceListPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
